import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Dialog, Icon, Layout, Text } from '@loadsmart/miranda-react'

import { useAsync } from 'core/ui/hooks/useAsync'

import { type ShipperAttachment } from '../../../domain/Document'
import { formatShipperAttachmentLabel } from '../../../presenters/shipperDocumentPresenter'
import { viewShipperAttachment } from '../../../use-cases/viewShipperAttachment'
import { SHIPPER_DETAIL_DRAWER_TOAST_ID } from '../ShipperDetailDrawer'

export type DocumentListItemProps = {
  attachment: ShipperAttachment
  shipperUuid: string
  onDelete?: () => void
}

export function DocumentListItem({ attachment, onDelete, shipperUuid }: DocumentListItemProps) {
  const { fileName, id } = attachment
  const label = formatShipperAttachmentLabel(attachment)
  const {
    call: viewAttachment,
    isPending: isOpeningAttachment,
    hasError,
    reset,
  } = useAsync(viewShipperAttachment)
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    if (hasError) {
      toast.error('Failed to open document', { containerId: SHIPPER_DETAIL_DRAWER_TOAST_ID })
      reset()
    }
  }, [hasError, reset])

  function handleViewClick() {
    viewAttachment({ shipperUuid, attachmentId: id })
  }

  function handleDeleteClick() {
    setConfirmOpen(true)
  }

  function handleConfirmDelete() {
    setConfirmOpen(false)
    onDelete?.()
  }

  function handleCancelDelete() {
    setConfirmOpen(false)
  }

  return (
    <Layout.Box
      key={`document-id-${attachment.id}`}
      borderWidth="border-thin"
      borderColor="color-border-input"
      borderRadius="border-radius-s"
      paddingX="spacing-4"
      paddingY="spacing-2"
    >
      <Layout.Group justify="space-between" align="center">
        <Text variant="body-md" color="color-text-tertiary">
          Uploaded
        </Text>
        <Layout.Group align="center" gap="spacing-2">
          <Button
            leading={<Icon name="arrow-corner-up" />}
            variant="tertiary"
            aria-label={`View ${fileName}`}
            loading={isOpeningAttachment}
            onClick={handleViewClick}
          >
            {fileName}
          </Button>
          <Text variant="body-sm" color="color-text-secondary">
            {label}
          </Text>
          {onDelete && (
            <Button
              variant="icon-secondary"
              size="small"
              aria-label={`Delete ${fileName}`}
              onClick={handleDeleteClick}
            >
              <Icon size="small" name="trash" />
            </Button>
          )}
        </Layout.Group>
      </Layout.Group>

      <Dialog
        open={confirmOpen}
        size="small"
        variant="danger"
        onClose={handleCancelDelete}
        data-testid="dialog-delete-document"
      >
        <Dialog.Header>Delete document</Dialog.Header>
        <Dialog.Body>
          <Text variant="body-md">
            Are you sure you want to delete this document? This action cannot be undone.
          </Text>
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionTertiary onClick={handleCancelDelete}>Cancel</Dialog.ActionTertiary>
          <Dialog.ActionPrimary variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Dialog.ActionPrimary>
        </Dialog.Actions>
      </Dialog>
    </Layout.Box>
  )
}
